import { useCallback, useState } from 'react'
import DevSection from './DevSection'
import { useDevPoll } from './useDevPoll'
import {
  getFootballStatus,
  getReplicaClock,
  injectReplicaError,
  jumpReplicaClock,
  postponeReplicaFixture,
  resetReplicaClock,
  runScenario,
  setReplicaClock,
  type ScenarioPreset,
} from '../../../api/dev'
import { refreshWorld } from '../../../app/worldRefresh'
import { useGoldenResults } from '../../../app/useGoldenResults'
import { teamPickerName } from '../../../teams'
import { isDevToolAvailable } from '../../../devToolCapabilities'
import type { DataMode } from '../../../dataMode'
import type { FootballStatusResponse, ReplicaClockState } from '../../../footballStatus'

/** Fast enough that a clock running at 60× visibly moves, cheap because it is a localhost hop. */
export const CLOCK_POLL_MS = 2_000

/** 60× plays a 90-minute match in 90 seconds — the speed you want to watch a reveal cascade at. */
const SPEEDS = [0, 1, 60, 300]

/**
 * The presets, and where each one lands you.
 *
 * `fresh-season` is absent on purpose: it is §5's factory reset, which lives in the Pipeline
 * section behind its own confirmation because it has a different blast radius and costs provider
 * requests. Every preset below only moves the clock.
 */
const PRESETS: { preset: Exclude<ScenarioPreset, 'fresh-season'>; label: string; title: string }[] = [
  {
    preset: 'before-deadline',
    label: 'Pre-deadline',
    title: 'Dev: 10 minutes before the gameweek locks',
  },
  {
    preset: 'at-deadline',
    label: 'At deadline',
    title: 'Dev: just past the lock — predictions frozen, auto-fill fired',
  },
  {
    preset: 'matchday-in-play',
    label: 'In play',
    title: 'Dev: first fixtures live, clock at 60× so the reveal cascade plays out',
  },
  {
    preset: 'full-time',
    label: 'Full time',
    title: 'Dev: every result in, before the advance window elapses',
  },
  {
    preset: 'settled',
    label: 'Settled',
    title: 'Dev: past settlement + 3h — points final, everyone advanced',
  },
  {
    preset: 'mid-season',
    label: 'Mid-season',
    title:
      'Dev: play gameweeks 1…N-1 through the pipeline one at a time, then sit on N (slow, on purpose)',
  },
]

/**
 * The replica's clock, in the rail.
 *
 * The clock *display* is the important half. Half the confusion in replica testing is not knowing
 * what time the replica thinks it is — every other control here is a way of changing that number,
 * and none of them make sense without seeing it.
 *
 * Nothing in this component knows where the replica lives: every call goes through our own server
 * (the guard rail), which is also what enforces that these only work in replica mode.
 */
export default function ReplicaSection({ mode, open }: { mode: DataMode | null; open: boolean }) {
  const available = isDevToolAvailable('replicaClock', mode)
  const showPresets = isDevToolAvailable('scenarioPreset', mode)
  const [note, setNote] = useState<string | null>(null)
  const [faultsOpen, setFaultsOpen] = useState(false)
  const [resultsOpen, setResultsOpen] = useState(false)
  const [fixtureId, setFixtureId] = useState('')
  const [presetGameweek, setPresetGameweek] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => getReplicaClock(), [])
  const { data, error, refresh } = useDevPoll<ReplicaClockState>(load, {
    enabled: open && available,
    intervalMs: CLOCK_POLL_MS,
  })

  /**
   * Where the season is anchored and what the current gameweek's flags say.
   *
   * Polled beside the clock rather than derived from it, because the clock is the *input* and these
   * three lines are the output — "did that jump actually lock the gameweek" is a question about
   * `gameweek_phase`, not about what time the replica thinks it is. Without them every preset is a
   * leap of faith and "did that work?" is answered by hunting through the app.
   */
  const loadStatus = useCallback(() => getFootballStatus(), [])
  const { data: status, refresh: refreshStatus } = useDevPoll<FootballStatusResponse>(loadStatus, {
    enabled: open && available,
    intervalMs: CLOCK_POLL_MS * 2,
  })

  /**
   * The whole gameweek's answer sheet, so what every card is going to do can be read at once
   * rather than one picker at a time. The rail is admin-only, so no extra gate is needed beyond
   * the rail being open. Fetched once per gameweek, not polled: the golden file does not change.
   */
  const golden = useGoldenResults({
    mode,
    gameweek: status?.seasonState?.gameweek ?? null,
    enabled: open && available,
  })

  if (!available) return null

  const run = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true)
    setNote(`${label}…`)
    try {
      const result = (await action()) as { jump?: { label: string }; label?: string }
      setNote(result?.jump ? `jumped to ${result.jump.label}` : (result?.label ?? label))
      refresh()
      refreshStatus()
      // The clock controls used to refresh the clock readout and nothing else, so a
      // jump that moved the whole season left the app rendering the world from before it. These are
      // pressed repeatedly, so they get the cheap path (re-read the season store) rather than the
      // page reload the resync controls take.
      await refreshWorld()
    } catch (failure) {
      setNote(failure instanceof Error ? failure.message : String(failure))
    } finally {
      setBusy(false)
    }
  }

  const seasonState = status?.seasonState
  const clock = status?.clock

  return (
    <DevSection title="Replica">
      <div className="gameweek-grid__dev-readout">
        {error !== null ? (
          <div className="gameweek-grid__dev-warning">replica clock unreachable: {error}</div>
        ) : (
          <>
            <div className="gameweek-grid__dev-readout-row">
              <span>replica now</span>
              <span>{data?.iso ?? '…'}</span>
            </div>
            <div className="gameweek-grid__dev-readout-row">
              <span>speed</span>
              <span>{data?.speed === null || data === null ? '…' : `${data.speed}×`}</span>
            </div>
          </>
        )}
        {/* Which clock the *server* is on. `replica-stale` or `real-fallback` here is the whole
            explanation for a jump that appears to do nothing, so it is said out loud. */}
        {clock && clock.source !== 'replica' && (
          <div className="gameweek-grid__dev-warning">
            server clock: {clock.source}
            {clock.error ? ` — ${clock.error}` : ''}
          </div>
        )}
        {seasonState && (
          <>
            <div className="gameweek-grid__dev-readout-row">
              <span>season anchor</span>
              <span>{seasonState.seasonAnchor ?? 'no fixtures'}</span>
            </div>
            <div className="gameweek-grid__dev-readout-row">
              <span>gameweek</span>
              <span>
                {seasonState.gameweek === null
                  ? '—'
                  : `GW${seasonState.gameweek} · ${
                      seasonState.deadlinePassed ? 'locked' : 'open'
                    } · ${seasonState.resultsRevealed ? 'results in' : 'awaiting results'}`}
              </span>
            </div>
            <div className="gameweek-grid__dev-readout-row">
              <span>settled at</span>
              <span>{seasonState.settledAt ?? 'not settled'}</span>
            </div>
          </>
        )}
      </div>

      {showPresets && (
        <>
          <p className="gameweek-grid__dev-tools-label label-section">Scenarios</p>
          <input
            className="gameweek-grid__dev-input"
            placeholder="gameweek (blank = current)"
            inputMode="numeric"
            value={presetGameweek}
            onChange={(event) => setPresetGameweek(event.target.value)}
            aria-label="Gameweek for the scenario presets"
          />
          <div className="gameweek-grid__dev-btn-row">
            {PRESETS.map((entry) => (
              <button
                key={entry.preset}
                className="gameweek-grid__dev-btn"
                disabled={busy}
                onClick={() =>
                  void run(entry.label, () => {
                    const parsed = Number(presetGameweek)
                    return runScenario(
                      entry.preset,
                      presetGameweek.trim() !== '' && Number.isFinite(parsed) ? parsed : undefined
                    )
                  })
                }
                title={entry.title}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Each jump lands one second past its target, so what you see next is the transition. */}
      <div className="gameweek-grid__dev-btn-row">
        <button
          className="gameweek-grid__dev-btn"
          onClick={() => void run('next deadline', () => jumpReplicaClock('next-deadline'))}
          title="Dev: jump the replica clock just past the next gameweek deadline"
        >
          ⏭ Deadline
        </button>
        <button
          className="gameweek-grid__dev-btn"
          onClick={() => void run('next kickoff', () => jumpReplicaClock('next-kickoff'))}
          title="Dev: jump just past the next kickoff"
        >
          ⏭ Kickoff
        </button>
        <button
          className="gameweek-grid__dev-btn"
          onClick={() => void run('full time', () => jumpReplicaClock('next-fulltime'))}
          title="Dev: jump past the current gameweek's last possible full time"
        >
          ⏭ Full time
        </button>
      </div>

      <div className="gameweek-grid__dev-btn-row">
        {[15, 60, 180].map((minutes) => (
          <button
            key={minutes}
            className="gameweek-grid__dev-btn"
            onClick={() => void run(`+${minutes}m`, () => setReplicaClock({ advanceMinutes: minutes }))}
            title={`Dev: advance the replica clock by ${minutes} minutes`}
          >
            +{minutes < 60 ? `${minutes}m` : `${minutes / 60}h`}
          </button>
        ))}
      </div>

      <div className="gameweek-grid__dev-btn-row">
        {SPEEDS.map((speed) => (
          <button
            key={speed}
            className="gameweek-grid__dev-btn"
            onClick={() => void run(`speed ${speed}×`, () => setReplicaClock({ speed }))}
            title={speed === 0 ? 'Dev: freeze the replica clock' : `Dev: run at ${speed}× real time`}
          >
            {speed}×
          </button>
        ))}
      </div>

      <button
        className="gameweek-grid__dev-btn"
        onClick={() => void run('clock reset', resetReplicaClock)}
        title="Dev: back to the replica's configured season start, frozen"
      >
        <span className="gameweek-grid__dev-btn-label">Reset clock</span>↺
      </button>

      {/* The answer sheet for the whole week. Collapsed by default — it is a spoiler, and the
          rail is read for the clock far more often than for this. */}
      <button
        className="gameweek-grid__dev-btn"
        onClick={() => setResultsOpen((was) => !was)}
        aria-expanded={resultsOpen}
      >
        <span className="gameweek-grid__dev-btn-label">Results</span>
        {resultsOpen ? '▾' : '▸'}
      </button>
      {resultsOpen && (
        <div className="gameweek-grid__dev-readout">
          {golden.fixtures.length === 0 ? (
            <div className="gameweek-grid__dev-readout-row">
              <span>no known results</span>
            </div>
          ) : (
            golden.fixtures.map((fixture) => (
              <div key={fixture.id} className="gameweek-grid__dev-readout-row">
                <span>
                  {teamPickerName(fixture.homeTeam)} v {teamPickerName(fixture.awayTeam)}
                </span>
                <span>
                  {fixture.homeScore}-{fixture.awayScore}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Behind a disclosure so it is not pressed by accident: these are the only ways to trigger
          a voided fixture or the scheduler's typed-error backoff on demand. */}
      <button
        className="gameweek-grid__dev-btn"
        onClick={() => setFaultsOpen((was) => !was)}
        aria-expanded={faultsOpen}
      >
        <span className="gameweek-grid__dev-btn-label">Faults</span>
        {faultsOpen ? '▾' : '▸'}
      </button>
      {faultsOpen && (
        <>
          <input
            className="gameweek-grid__dev-input"
            placeholder="fixture id (e.g. gw1-1)"
            value={fixtureId}
            onChange={(event) => setFixtureId(event.target.value)}
            aria-label="Fixture to postpone"
          />
          <div className="gameweek-grid__dev-btn-row">
            <button
              className="gameweek-grid__dev-btn"
              disabled={fixtureId === ''}
              onClick={() => void run('postponed', () => postponeReplicaFixture(fixtureId))}
              title="Dev: void this fixture in the replica"
            >
              Postpone
            </button>
            <button
              className="gameweek-grid__dev-btn"
              disabled={fixtureId === ''}
              onClick={() => void run('restored', () => postponeReplicaFixture(fixtureId, true))}
              title="Dev: un-void it"
            >
              Restore
            </button>
          </div>
          <div className="gameweek-grid__dev-btn-row">
            <button
              className="gameweek-grid__dev-btn"
              onClick={() => void run('error injected', () => injectReplicaError('fixtures', '500'))}
              title="Dev: make the replica's /fixtures endpoint fail"
            >
              Force error
            </button>
            <button
              className="gameweek-grid__dev-btn"
              onClick={() => void run('errors cleared', () => injectReplicaError('fixtures', 'clear'))}
              title="Dev: stop failing"
            >
              Clear
            </button>
          </div>
        </>
      )}

      {note !== null && <div className="gameweek-grid__dev-readout">{note}</div>}
    </DevSection>
  )
}
